"use client";

import { useTranslations } from "next-intl";
import { ArrowLeft, Map, Home, HelpCircle, BookOpen, FileText, Heart } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { LoveLanguage } from "@/types";

const loveLanguages = Object.values(LoveLanguage);

export function SitemapClient() {
  const t = useTranslations();

  const mainPages = [
    { href: "/", label: t("app_name"), icon: Home, desc: t("sitemap_home_desc") },
    { href: "/quiz/instructions", label: t("instructions_title"), icon: HelpCircle, desc: t("sitemap_instructions_desc") },
    { href: "/quiz", label: t("sitemap_quiz"), icon: Heart, desc: t("sitemap_quiz_desc") },
    { href: "/results", label: t("sitemap_results"), icon: Heart, desc: t("sitemap_results_desc") },
  ];

  const infoPages = [
    { href: "/about", label: t("footer_about"), desc: t("sitemap_about_desc") },
    { href: "/contact", label: t("footer_contact"), desc: t("sitemap_contact_desc") },
    { href: "/privacy", label: t("footer_privacy"), desc: t("sitemap_privacy_desc") },
    { href: "/terms", label: t("footer_terms"), desc: t("sitemap_terms_desc") },
  ];

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8" data-testid="sitemap-page">
      <Link
        href="/"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        data-testid="sitemap-back"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("btn_back")}
      </Link>

      <div className="bg-card border border-border rounded-3xl p-8 md:p-10 card-elevated">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Map className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
              {t("sitemap_title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("sitemap_subtitle")}</p>
          </div>
        </div>

        <div className="space-y-10">
          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-primary" />
              {t("sitemap_main_pages")}
            </h2>
            <ul className="space-y-3">
              {mainPages.map((page) => (
                <li key={page.href}>
                  <Link
                    href={page.href}
                    className="flex items-center justify-between p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <page.icon className="w-5 h-5 text-primary" />
                      <div>
                        <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                          {page.label}
                        </span>
                        <p className="text-sm text-muted-foreground">{page.desc}</p>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              {t("sitemap_guide_section")}
            </h2>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/guide"
                  className="flex items-center justify-between p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                >
                  <div>
                    <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                      {t("info_menu_title")}
                    </span>
                    <p className="text-sm text-muted-foreground">{t("sitemap_guide_desc")}</p>
                  </div>
                </Link>
              </li>
              {loveLanguages.map((lang) => (
                <li key={lang} className="ml-6">
                  <Link
                    href={`/guide/${lang}`}
                    className="flex items-center p-3 rounded-xl hover:bg-secondary/50 transition-colors group"
                  >
                    <span className="text-foreground/80 group-hover:text-primary transition-colors">
                      {t(`lang_${lang}`)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {t("sitemap_info_section")}
            </h2>
            <ul className="space-y-3">
              {infoPages.map((page) => (
                <li key={page.href}>
                  <Link
                    href={page.href}
                    className="flex items-center justify-between p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                  >
                    <div>
                      <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {page.label}
                      </span>
                      <p className="text-sm text-muted-foreground">{page.desc}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
